import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Trophy, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { motion } from "framer-motion";

interface GibRatingChange {
  userId: string;
  displayName: string;
  before: number;
  after: number;
  contributionsCount: number;
  averageRating: number;
}

interface SprintGibRatingSummaryProps {
  sprintId: string;
}

export const SprintGibRatingSummary = ({ sprintId }: SprintGibRatingSummaryProps) => {
  const [changes, setChanges] = useState<GibRatingChange[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGibRatingChanges();
  }, [sprintId]);

  const loadGibRatingChanges = async () => {
    try {
      // Get all contributors for this sprint
      const { data: contributions } = await supabase
        .from('sprint_contributions')
        .select(`
          contributor_id,
          profiles:contributor_id (display_name)
        `)
        .eq('sprint_id', sprintId)
        .eq('stage', 'execute');

      if (!contributions) return;

      const contributorIds = [...new Set(contributions.map(c => c.contributor_id))];
      const changesData: GibRatingChange[] = [];

      for (const userId of contributorIds) {
        // Get GibRating data
        const { data: gibData } = await supabase
          .from('user_gib_ratings')
          .select('overall_rating')
          .eq('user_id', userId)
          .single();

        // Get contribution stats
        const { data: userContributions } = await supabase
          .from('sprint_contributions')
          .select('id, contributor_rating')
          .eq('sprint_id', sprintId)
          .eq('contributor_id', userId)
          .eq('stage', 'execute');

        // Get profile data
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', userId)
          .single();

        const avgRating = userContributions && userContributions.length > 0
          ? userContributions.reduce((sum, c) => sum + (c.contributor_rating || 0), 0) / userContributions.length
          : 0;

        changesData.push({
          userId,
          displayName: profile?.display_name || 'Unknown',
          before: 50, // Mock "before" value - in real app, store this at sprint start
          after: gibData?.overall_rating || 50,
          contributionsCount: userContributions?.length || 0,
          averageRating: avgRating
        });
      }

      // Sort by rating change (highest improvement first)
      changesData.sort((a, b) => (b.after - b.before) - (a.after - a.before));
      setChanges(changesData);
    } catch (error) {
      console.error('Error loading GibRating changes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRatingTrend = (before: number, after: number) => {
    const diff = after - before;
    if (diff > 0) return { icon: TrendingUp, color: 'text-green-500', text: `+${diff.toFixed(1)}` };
    if (diff < 0) return { icon: TrendingDown, color: 'text-red-500', text: diff.toFixed(1) };
    return { icon: Minus, color: 'text-muted-foreground', text: '0.0' };
  };

  if (loading) return null;

  const topPerformer = changes[0];

  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-bold mb-2">🎉 Sprint Complete!</h3>
          <p className="text-muted-foreground">
            GibRatings have been updated for all participants
          </p>
        </div>

        {topPerformer && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
              <div className="flex items-center gap-3">
                <Trophy className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Sprint MVP</p>
                  <p className="font-bold">{topPerformer.displayName}</p>
                  <p className="text-sm text-muted-foreground">
                    Highest rating improvement: {getRatingTrend(topPerformer.before, topPerformer.after).text}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        <div className="space-y-3">
          <h4 className="font-semibold text-sm">Participant GibRating Changes</h4>
          {changes.map((change, index) => {
            const trend = getRatingTrend(change.before, change.after);
            const TrendIcon = trend.icon;

            return (
              <motion.div
                key={change.userId}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>
                          {change.displayName.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{change.displayName}</p>
                        <p className="text-xs text-muted-foreground">
                          {change.contributionsCount} contribution{change.contributionsCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">{change.before.toFixed(1)}</span>
                          <span className="text-lg font-bold">→</span>
                          <span className="text-sm font-bold">{change.after.toFixed(1)}</span>
                        </div>
                      </div>
                      <Badge variant="secondary" className={`${trend.color} flex items-center gap-1`}>
                        <TrendIcon className="w-3 h-3" />
                        {trend.text}
                      </Badge>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};
